/**
 * The calculation behind a cell: a small check table run in the table's own session, with the
 * statistics that explain the cell's number (a mean is its sum over its base, a column % its count
 * over the column's base), and the sum the trace panel shows from them. Pure: the panel sends the
 * script through the feature that owns the session.
 *
 * The check runs the table's script again with one table added right after the table's own line:
 * the same rows, columns and base (UNIVERSE:) under `TRACE_CHECK`, with the statistics the check
 * needs. The script declares and removes its own helpers, and its USEWEIGHTS lines set the weight
 * the table ran with; only the check table is output, and it is removed at once. A script that set
 * a weight ends with the preparation's weight line, so the session is left as it was.
 */

import type { EngineOutputItem, RTableContent } from "types/engineOutput.types"
import { buildTableModel } from "utils/tableModel"
import type { AxisLeaf, TableModel } from "utils/tableModel"
import { normLabel } from "./lineageLabels"
import { TRACE_CHECK } from "./traceTable"

/** How a statistic is worked out. */
export type CheckKind = "mean" | "share" | "count"

/** Which base a share divides by. */
type ShareOf = "column" | "row" | "all" | "answered"

export interface CheckPlan {
  kind: CheckKind
  /** The cell's statistic as the engine names it. */
  stat: string
  /** What the check table asks for. */
  operators: string[]
  weighted: boolean
  /** The statistic is shown as a percentage. */
  percent: boolean
  /** For a share and a count: the base it is out of. */
  of?: ShareOf
}

/**
 * The check for statistic `stat`, or null for one the check does not explain (an index, a
 * median, a standard deviation, a statistic the client works out itself).
 */
export const checkPlan = (stat: string | undefined, weighted: boolean): CheckPlan | null => {
  const plan = (kind: CheckKind, operators: string[], percent: boolean, of?: ShareOf): CheckPlan => ({
    kind,
    stat: stat ?? "",
    operators,
    weighted,
    percent,
    ...(of ? { of } : {}),
  })
  const counts = weighted ? ["n", "N"] : ["n"]
  switch (stat) {
    case "avg":
      return plan("mean", weighted ? ["base n", "base N", "sum", "avg"] : ["base n", "sum", "avg"], false)
    case "perc":
      return plan("share", weighted ? ["base n", "base N", "n", "N", "perc"] : ["base n", "n", "perc"], true, "answered")
    case "col%":
      return plan("share", [...counts, "col%"], true, "column")
    case "row%":
      return plan("share", [...counts, "row%"], true, "row")
    case "perc(%)":
      return plan("share", [...counts, "perc(%)"], true, "all")
    case "n":
    case "N":
      return plan("count", counts, false, "column")
    case "base n":
    case "base N":
      return plan("count", weighted ? ["base n", "base N"] : ["base n"], false)
    default:
      return null
  }
}

const TOKEN = /<<.*?>>|"[^"]*"|\S+/g
const KEYWORD = /^[A-Za-z]+:/

/**
 * The script that works out the cell's numbers: `tableScript` with the check table after the last
 * XTAB or DTAB line that writes `table`, and no output but the check's (see the file's comment).
 * `restoreWeight` is the weight line in effect before the script ran. Null when the script has no
 * line for the table.
 */
export const traceCheckScript = (
  tableScript: string,
  table: string,
  plan: CheckPlan,
  restoreWeight: string
): string | null => {
  const lines = tableScript.split(/\r?\n/)
  const want = normLabel(table)
  let at = -1
  let check = ""
  lines.forEach((line, i) => {
    const m = /^\s*(XTAB|DTAB)\s+(\S+)(.*)$/i.exec(line)
    if (!m || normLabel(m[2]) !== want) return
    const tokens = m[3].match(TOKEN) ?? []
    // Rows and columns come first; of the options after them only UNIVERSE: is kept.
    const first = tokens.findIndex((t) => KEYWORD.test(t))
    const axes = first < 0 ? tokens : tokens.slice(0, first)
    const u = tokens.findIndex((t) => /^UNIVERSE:/i.test(t))
    const universe = u < 0 ? null : tokens[u].slice("UNIVERSE:".length) || (tokens[u + 1] ?? null)
    at = i
    const operators = plan.operators.map((o) => `"${o}"`).join(", ")
    check = `${m[1].toUpperCase()} ${TRACE_CHECK} ${axes.join(" ")}${universe ? ` UNIVERSE: ${universe}` : ""} OPERATORS: ${operators}`
  })
  if (at < 0) return null
  // The check is output as the table was, so its rows and columns run the same way.
  const output = lines.slice(at + 1).find((l) => {
    const m = /^\s*OUTPUT\s+(\S+)/i.exec(l)
    return m !== null && normLabel(m[1]) === want
  })
  const flags = output ? output.trim().split(/\s+/).slice(2).join(" ") : ""
  const out: string[] = []
  let weighs = false
  lines.forEach((line, i) => {
    if (/^\s*OUTPUT\b/i.test(line)) return
    if (/^\s*USEWEIGHTS\b/i.test(line)) weighs = true
    out.push(line)
    if (i === at) out.push(check, `OUTPUT ${TRACE_CHECK}${flags ? ` ${flags}` : ""}`, `REMOVE ${TRACE_CHECK}`)
  })
  if (weighs) out.push(restoreWeight)
  return out.join("\n")
}

/** The weight line in effect after the preparation: its last USEWEIGHTS, else none. */
export const preparationWeight = (scripts: string[]): string => {
  let found = "USEWEIGHTS"
  scripts.forEach((script) =>
    script.split(/\r?\n/).forEach((line) => {
      if (/^\s*USEWEIGHTS\b/i.test(line)) found = line.trim()
    })
  )
  return found
}

// ---------------------------------------------------------------------------
// Reading the check
// ---------------------------------------------------------------------------

/** The numbers behind the cell. */
export interface CheckNumbers {
  plan: CheckPlan
  /** The sum of the values (a mean), the count of the cell (a share, a count). */
  numerator: number
  /** What it is divided by: the base. Absent for a count. */
  denominator?: number
  /** The cell's statistic worked out from them (a share as a fraction). */
  result: number
  /** The people in the base, unweighted, when the check has them. */
  people?: number
}

export class CheckError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "CheckError"
  }
}

const labelsOf = (path: string[]): string[] => path.filter((p) => p !== "").map(normLabel)

/** The categories of an axis, each once: the leaves without their statistic. */
const categoriesOf = (leaves: AxisLeaf[]): AxisLeaf[] => {
  const seen = new Set<string>()
  return leaves.filter((l) => {
    const key = `${l.isTotal}|${labelsOf(l.path).join("//")}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * The category of `leaves` that stands for `want`: the Totals for the Totals; else the one whose
 * labels end with those of `want` (or the other way round), the longest match first. An axis of
 * one category stands for anything (a DTAB over the Total table has no Totals of its own).
 */
const findCategory = (leaves: AxisLeaf[], want: AxisLeaf | "total"): AxisLeaf | null => {
  const categories = categoriesOf(leaves)
  const single = categories.length === 1 ? categories[0] : null
  if (want === "total" || want.isTotal) return categories.find((l) => l.isTotal) ?? single
  const target = labelsOf(want.path)
  let best: AxisLeaf | null = null
  let bestLength = 0
  let tie = false
  for (const l of categories) {
    const labels = labelsOf(l.path)
    const n = Math.min(labels.length, target.length)
    if (l.isTotal || n === 0) continue
    if (labels.slice(labels.length - n).join("//") !== target.slice(target.length - n).join("//")) continue
    if (n > bestLength) {
      best = l
      bestLength = n
      tie = false
    } else if (n === bestLength) tie = true
  }
  if (best && !tie) return best
  return single
}

const sameCategory = (a: AxisLeaf, b: AxisLeaf): boolean =>
  a.isTotal === b.isTotal && labelsOf(a.path).join("//") === labelsOf(b.path).join("//")

/** The number at (`down`, `across`) for statistic `stat`, or null when the check has none. */
const valueAt = (model: TableModel, down: AxisLeaf, across: AxisLeaf, stat: string): number | null => {
  const r = model.down.leaves.findIndex(
    (l) => sameCategory(l, down) && (model.statAxis !== "down" || l.stat === stat)
  )
  const c = model.across.leaves.findIndex(
    (l) => sameCategory(l, across) && (model.statAxis !== "across" || l.stat === stat)
  )
  if (r < 0 || c < 0) return null
  const raw = model.cells[r]?.[c]?.raw
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null
}

/**
 * The numbers behind the cell (`down`, `across`) from the check's output. `transposed`: the
 * table's rows run across, as the shown table's did. Throws a CheckError when the output has no
 * table, or not the cell.
 */
export const readCheck = (
  output: EngineOutputItem[],
  plan: CheckPlan,
  down: AxisLeaf,
  across: AxisLeaf,
  transposed: boolean
): CheckNumbers => {
  const item =
    output.find((o) => o.label !== undefined && normLabel(o.label) === normLabel(TRACE_CHECK)) ??
    output.find((o) => Array.isArray((o.content as RTableContent | undefined)?.data))
  if (!item) throw new CheckError("the check gave no table.")
  const model = buildTableModel(item.content as RTableContent)
  const row = findCategory(model.down.leaves, down)
  const column = findCategory(model.across.leaves, across)
  if (!row || !column) throw new CheckError("the cell was not found in the check.")

  /** A number of the check, by statistic, at the cell or at one of its bases. */
  const at = (stat: string, where: { down?: "total"; across?: "total" } = {}): number => {
    const d = where.down ? findCategory(model.down.leaves, "total") : row
    const a = where.across ? findCategory(model.across.leaves, "total") : column
    const v = d && a ? valueAt(model, d, a, stat) : null
    if (v === null) throw new CheckError(`the check has no "${stat}" for this cell.`)
    return v
  }
  const tryAt = (stat: string, where: { down?: "total"; across?: "total" } = {}): number | undefined => {
    try {
      return at(stat, where)
    } catch {
      return undefined
    }
  }
  const count = plan.weighted ? "N" : "n"
  // The engine's rows run down unless the table was output transposed.
  const rowsTotal = transposed ? { across: "total" as const } : { down: "total" as const }
  const columnsTotal = transposed ? { down: "total" as const } : { across: "total" as const }

  switch (plan.kind) {
    case "mean": {
      const numerator = at("sum")
      const denominator = at(plan.weighted ? "base N" : "base n")
      return { plan, numerator, denominator, result: numerator / denominator, people: tryAt("base n") }
    }
    case "share": {
      if (plan.of === "answered") {
        const numerator = at(count)
        const denominator = at(plan.weighted ? "base N" : "base n")
        return { plan, numerator, denominator, result: numerator / denominator, people: tryAt("base n") }
      }
      // A column's base is the Totals of the rows, a row's the Totals of the columns.
      const where =
        plan.of === "column" ? rowsTotal : plan.of === "row" ? columnsTotal : { down: "total" as const, across: "total" as const }
      const numerator = at(count)
      const denominator = at(count, where)
      return { plan, numerator, denominator, result: numerator / denominator, people: tryAt("n", where) }
    }
    case "count": {
      const numerator = at(plan.stat)
      if (plan.stat === "base n" || plan.stat === "base N") return { plan, numerator, result: numerator }
      return { plan, numerator, result: numerator, people: tryAt("n", rowsTotal) }
    }
  }
}

/**
 * Whether `result` shows as `shown`, rounded as the table rounded it: null when `shown` is not a
 * number. A share is compared as a percentage.
 */
export const matchesShown = (shown: string | undefined, result: number, percent: boolean): boolean | null => {
  if (!shown) return null
  const text = shown.replace(/[,\s%]/g, "")
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null
  const decimals = text.includes(".") ? text.length - text.indexOf(".") - 1 : 0
  const value = percent ? result * 100 : result
  return Number(value.toFixed(decimals)) === Number(text)
}

/** How many decimals `shown` has: the table's rounding, for the result of the sum. */
export const shownDecimals = (shown: string | undefined, fallback: number): number => {
  const text = shown?.replace(/[,\s%]/g, "") ?? ""
  if (!/^-?\d+(\.\d+)?$/.test(text)) return fallback
  return text.includes(".") ? text.length - text.indexOf(".") - 1 : 0
}
